import React, { useState, useEffect, useCallback, useMemo } from 'react';
import StudentDetailPanel from './StudentDetailPanel';
import {
  Home,
  Calendar as CalendarIcon,
  Users,
  BookOpen,
  ClipboardList,
  TrendingUp,
  Settings,
  Clock,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  User,
  ArrowLeft,
  Info,
  AlertCircle,
  X,
  Search,
  Filter,
  Plus,
  Download,
  RefreshCw,
  MoreVertical,
  Edit,
  Trash2,
  Eye
} from 'lucide-react';

const LessonCalendarDashboard = () => {
  // State management
  const [lessons, setLessons] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedLessons, setSelectedLessons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [activeView, setActiveView] = useState('dashboard');
  const [isStudentPanelOpen, setIsStudentPanelOpen] = useState(false);
  const [studentForPanel, setStudentForPanel] = useState(null);
  
  // New state for enhanced features
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Dashboard statistics
  const [dashboardStats, setDashboardStats] = useState({
    totalLessons: 0,
    upcomingLessons: 0,
    completedLessons: 0,
    canceledLessons: 0,
    thisWeekLessons: 0,
    studentCount: 0,
    subjectBreakdown: {},
    lessonsPerDay: Array(7).fill(0),
    recentStudents: [],
    monthlyTrend: []
  });

  // View options for the subcomponent
  const viewOptions = [
    { id: 'dashboard', label: 'Dashboard', icon: <Home className="w-5 h-5" /> },
    { id: 'calendar', label: 'Calendar', icon: <CalendarIcon className="w-5 h-5" /> },
    { id: 'lessons', label: 'All Lessons', icon: <BookOpen className="w-5 h-5" /> }
  ];

  // Fetch students data
  const fetchStudents = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/students/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch students');
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching students:', error);
      throw error;
    }
  }, []);

  // Memoized filtered and sorted lessons
  const filteredLessons = useMemo(() => {
    let filtered = lessons.filter(lesson => {
      const matchesSearch = searchTerm === '' || 
        lesson.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${lesson.studentFirstName} ${lesson.studentLastName}`.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || lesson.status === statusFilter;
      const matchesSubject = subjectFilter === 'all' || lesson.subject === subjectFilter;
      
      return matchesSearch && matchesStatus && matchesSubject;
    });

    // Sort lessons
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'date':
          aValue = new Date(a.dateTime);
          bValue = new Date(b.dateTime);
          break;
        case 'student':
          aValue = `${a.studentFirstName} ${a.studentLastName}`;
          bValue = `${b.studentFirstName} ${b.studentLastName}`;
          break;
        case 'subject':
          aValue = a.subject || '';
          bValue = b.subject || '';
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [lessons, searchTerm, statusFilter, subjectFilter, sortBy, sortOrder]);

  // Get unique subjects for filter
  const uniqueSubjects = useMemo(() => {
    const subjects = [...new Set(lessons.map(lesson => lesson.subject).filter(Boolean))];
    return subjects.sort();
  }, [lessons]);

  // Fetch lessons data with better error handling
  const fetchLessons = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setIsLoading(true);
      setError(null);
      
      // Check for auth token in memory state instead of localStorage
      const token = localStorage?.getItem('auth_token');
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      const [lessonsResponse, studentsResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/lessons/tutor/me`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetchStudents()
      ]);
      
      if (lessonsResponse.status === 401 || studentsResponse.status === 401) {
        throw new Error('Session expired. Please log in again.');
      }
      
      if (!lessonsResponse.ok) {
        throw new Error(`Failed to fetch lessons (${lessonsResponse.status})`);
      }
      
      const lessonsData = await lessonsResponse.json();
      const studentsData = await studentsResponse;

      // Format lessons with Date objects and better validation
      const formattedLessons = lessonsData.map(lesson => {
        const dateTime = new Date(lesson.dateTime);
        if (isNaN(dateTime.getTime())) {
          console.warn('Invalid date for lesson:', lesson.id);
          return null;
        }
        
        return {
          ...lesson,
          dateObj: dateTime,
          endTime: new Date(dateTime.getTime() + (lesson.duration || 60) * 60000),
          subject: lesson.subject || 'Untitled',
          status: lesson.status || 'Unknown'
        };
      }).filter(Boolean);

      setLessons(formattedLessons);
      calculateDashboardStats(formattedLessons, studentsData);
      
    } catch (err) {
      setError(err.message);
      console.error('Error fetching data:', err);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [fetchStudents]);

  // Refresh data
  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    fetchLessons(false);
  }, [fetchLessons]);

  // Initial data fetch
  useEffect(() => {
    fetchLessons();
  }, [fetchLessons]);

  // Calculate dashboard statistics with enhancements
  const calculateDashboardStats = useCallback((lessonData, studentsData) => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    // Calculate monthly trend (last 6 months)
    const monthlyTrend = [];
    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
      const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
      
      const lessonsInMonth = lessonData.filter(lesson => {
        const lessonDate = new Date(lesson.dateTime);
        return lessonDate >= monthStart && lessonDate <= monthEnd;
      }).length;
      
      monthlyTrend.push({
        month: monthDate.toLocaleDateString('default', { month: 'short' }),
        lessons: lessonsInMonth
      });
    }

    const stats = {
      totalLessons: lessonData.length,
      upcomingLessons: 0,
      completedLessons: 0,
      canceledLessons: 0,
      thisWeekLessons: 0,
      studentCount: new Set(),
      subjectBreakdown: {},
      lessonsPerDay: Array(7).fill(0),
      recentStudents: [],
      monthlyTrend
    };

    const studentMap = new Map();

    // Map students data first
    studentsData.forEach(student => {
      studentMap.set(student.id, {
        id: student.id,
        firstName: student.firstName,
        lastName: student.lastName,
        email: student.email,
        lastLessonDate: null,
        lessonCount: 0,
        subjects: new Set()
      });
    });

    lessonData.forEach(lesson => {
      // Count by status
      if (lesson.status === 'Upcoming') stats.upcomingLessons++;
      else if (lesson.status === 'Completed') stats.completedLessons++;
      else if (lesson.status === 'Canceled') stats.canceledLessons++;
      
      // Count unique students
      stats.studentCount.add(lesson.studentId);
      
      // Update student data if exists
      if (studentMap.has(lesson.studentId)) {
        const student = studentMap.get(lesson.studentId);
        student.lessonCount++;
        student.subjects.add(lesson.subject);
        const lessonDate = new Date(lesson.dateTime);
        if (!student.lastLessonDate || lessonDate > student.lastLessonDate) {
          student.lastLessonDate = lessonDate;
        }
      }
      
      // Count by subject
      const subject = lesson.subject || 'Unspecified';
      stats.subjectBreakdown[subject] = (stats.subjectBreakdown[subject] || 0) + 1;
      
      // Count by day of week
      const lessonDate = new Date(lesson.dateTime);
      const dayOfWeek = lessonDate.getDay();
      stats.lessonsPerDay[dayOfWeek]++;
      
      // Count this week's lessons
      if (lessonDate >= startOfWeek && lessonDate <= endOfWeek) {
        stats.thisWeekLessons++;
      }
    });

    // Convert student data for recent students
    stats.studentCount = stats.studentCount.size;
    stats.recentStudents = Array.from(studentMap.values())
      .filter(student => student.lastLessonDate !== null)
      .sort((a, b) => b.lastLessonDate - a.lastLessonDate)
      .slice(0, 5)
      .map(student => ({
        ...student,
        subjects: Array.from(student.subjects)
      }));
    
    setDashboardStats(stats);
  }, []);

  // Calendar helpers
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();
  
  const formatDate = (date) => date.toISOString().split('T')[0];
  const formatTime = (date) => date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  
  const handleDateClick = (day) => {
    if (day === 0) return;
    
    const clickedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    setSelectedDate(clickedDate);
    
    const formattedDate = formatDate(clickedDate);
    const lessonsOnDate = lessons.filter(lesson => 
      formatDate(new Date(lesson.dateTime)) === formattedDate
    );
    
    setSelectedLessons(lessonsOnDate);
    setActiveView('day');
  };

  const handleMonthChange = (months) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + months, 1));
  };

  // Lesson and student handlers
  const handleLessonClick = (lesson) => setSelectedLesson(lesson);
  const closeModal = () => setSelectedLesson(null);

  const handleOpenStudentPanel = async (studentId) => {
    try {
      const token = localStorage?.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/students/me/${studentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch student');
      
      const studentData = await response.json();
      setStudentForPanel(studentData);
      setIsStudentPanelOpen(true);
    } catch (error) {
      console.error('Error fetching student:', error);
      setError('Failed to load student details');
    }
  };

  const handleClosePanel = () => {
    setIsStudentPanelOpen(false);
    setStudentForPanel(null);
  };

  // UI Components
  const StatusBadge = ({ status }) => {
    const getStatusConfig = (status) => {
      switch (status) {
        case 'Upcoming':
          return { bg: 'bg-blue-100', text: 'text-blue-800', icon: <Clock className="w-3 h-3" /> };
        case 'Completed':
          return { bg: 'bg-green-100', text: 'text-green-800', icon: <CheckCircle className="w-3 h-3" /> };
        case 'Canceled':
          return { bg: 'bg-red-100', text: 'text-red-800', icon: <XCircle className="w-3 h-3" /> };
        default:
          return { bg: 'bg-gray-100', text: 'text-gray-800', icon: <AlertCircle className="w-3 h-3" /> };
      }
    };

    const config = getStatusConfig(status);
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.icon}
        <span className="ml-1">{status}</span>
      </span>
    );
  };

  const DashboardCard = ({ title, value, icon, color, subtitle, trend }) => (
    <div className={`bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow ${color}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-3xl font-bold mt-2 text-gray-900">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
          {trend && (
            <div className="flex items-center mt-2">
              <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
              <span className="text-sm text-green-600">{trend}</span>
            </div>
          )}
        </div>
        <div className="p-3 rounded-full bg-gray-50">
          {React.cloneElement(icon, { className: 'w-6 h-6 text-gray-600' })}
        </div>
      </div>
    </div>
  );

  const LessonCard = ({ lesson, onClick, showDate = false }) => {
    const startTime = new Date(lesson.dateTime);
    const endTime = new Date(startTime.getTime() + lesson.duration * 60000);
    
    return (
      <div 
        onClick={onClick}
        className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:border-gray-300 transition-all cursor-pointer group"
      >
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
              {lesson.subject}
            </h4>
            <div className="flex items-center mt-2 text-sm text-gray-500">
              <Clock className="w-4 h-4 mr-1" />
              <span>
                {showDate && `${formatDate(startTime)} • `}
                {formatTime(startTime)} - {formatTime(endTime)}
              </span>
              <span className="mx-2">•</span>
              <span>{lesson.duration} min</span>
            </div>
          </div>
          <StatusBadge status={lesson.status} />
        </div>
        
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center text-sm text-gray-600">
            <User className="w-4 h-4 mr-1" />
            <span>{lesson.studentFirstName} {lesson.studentLastName}</span>
          </div>
          <button 
            className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200 transition-all"
            onClick={(e) => {
              e.stopPropagation();
              // Handle more options
            }}
          >
            <MoreVertical className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  const FilterBar = () => (
<div className="bg-white w-full p-4 rounded-lg border border-gray-200 mb-6">
  <div className="flex flex-col sm:flex-row gap-4">
    <div className="flex-1 min-w-0">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          placeholder="Search lessons or students..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
    </div>
    
    <div className="flex gap-2 flex-shrink-0">
      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-32"
      >
        <option value="all">All Status</option>
        <option value="Upcoming">Upcoming</option>
        <option value="Completed">Completed</option>
        <option value="Canceled">Canceled</option>
      </select>
      
      <select
        value={subjectFilter}
        onChange={(e) => setSubjectFilter(e.target.value)}
        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-36"
      >
        <option value="all">All Subjects</option>
        {uniqueSubjects.map(subject => (
          <option key={subject} value={subject}>{subject}</option>
        ))}
      </select>
      
      <button
        onClick={handleRefresh}
        disabled={isRefreshing}
        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center flex-shrink-0"
      >
        <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
      </button>
    </div>
  </div>
</div>
  );

  // Enhanced dashboard view
  const renderDashboard = () => (
    <div className="space-y-6">
      <FilterBar />
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <DashboardCard 
          title="Total Lessons" 
          value={dashboardStats.totalLessons} 
          icon={<BookOpen />} 
          color="border-l-4 border-blue-500"
          subtitle="All time"
        />
        <DashboardCard 
          title="Upcoming" 
          value={dashboardStats.upcomingLessons} 
          icon={<Clock />} 
          color="border-l-4 border-amber-500" 
          subtitle="Scheduled lessons"
        />
        <DashboardCard 
          title="Active Students" 
          value={dashboardStats.studentCount} 
          icon={<Users />} 
          color="border-l-4 border-green-500"
          subtitle="Unique students"
        />
        <DashboardCard 
          title="This Week" 
          value={dashboardStats.thisWeekLessons} 
          icon={<CalendarIcon />} 
          color="border-l-4 border-purple-500"
          subtitle="Weekly progress"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Enhanced Weekly Schedule */}
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <h3 className="font-semibold text-lg mb-4 text-gray-900">Weekly Schedule</h3>
          <WeeklyChart data={dashboardStats.lessonsPerDay} />
        </div>

        {/* Enhanced Recent Students */}
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <h3 className="font-semibold text-lg mb-4 text-gray-900">Recent Students</h3>
          <div className="space-y-3">
            {dashboardStats.recentStudents.length > 0 ? (
              dashboardStats.recentStudents.map(student => (
                <div 
                  key={student.id}
                  onClick={() => handleOpenStudentPanel(student.id)}
                  className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors group"
                >
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center mr-3 font-semibold">
                    {student.firstName?.charAt(0) || 'S'}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 group-hover:text-blue-600">
                      {student.firstName} {student.lastName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {student.lessonCount} lesson{student.lessonCount !== 1 ? 's' : ''} • {student.subjects.slice(0, 2).join(', ')}
                    </p>
                  </div>
                  <Eye className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-sm text-center py-4">No recent students</p>
            )}
          </div>
        </div>

        {/* Enhanced Subject Breakdown */}
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <h3 className="font-semibold text-lg mb-4 text-gray-900">Subject Distribution</h3>
          <div className="space-y-3">
            {Object.entries(dashboardStats.subjectBreakdown).map(([subject, count]) => {
              const percentage = dashboardStats.totalLessons > 0 ? Math.round((count / dashboardStats.totalLessons) * 100) : 0;
              return (
                <div key={subject} className="mb-3">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium text-gray-700">{subject}</span>
                    <span className="text-gray-500">{count} ({percentage}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500" 
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Enhanced Upcoming Lessons */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-lg text-gray-900">Upcoming Lessons</h3>
          <button 
            onClick={() => setActiveView('lessons')}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            View All
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredLessons
            .filter(lesson => lesson.status === 'Upcoming')
            .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime))
            .slice(0, 6)
            .map(lesson => (
              <LessonCard 
                key={lesson.id} 
                lesson={lesson} 
                onClick={() => handleLessonClick(lesson)}
                showDate={true}
              />
            ))}
          {filteredLessons.filter(lesson => lesson.status === 'Upcoming').length === 0 && (
            <div className="col-span-full text-center py-8 text-gray-500">
              <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No upcoming lessons scheduled</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

 const WeeklyChart = ({ data }) => {
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const maxValue = Math.max(...data, 1);
        
  return (
    <div className="bg-white rounded-xl p-6 shadow-lg mx-auto max-w-4xl flex flex-col justify-center h-fit">
      <div className="flex items-end h-48 pt-8 gap-3">
        {data.map((count, index) => (
          <div key={index} className="flex-1 flex flex-col items-center group">
            <div className="w-full flex flex-col items-center relative">
              {/* Tooltip */}
              <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 whitespace-nowrap pointer-events-none z-10 shadow-lg">
                <span className="font-medium">{count} lesson{count !== 1 ? 's' : ''}</span>
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
              </div>
              
              {/* Bar */}
              <div
                className="w-full bg-gradient-to-t from-blue-600 to-blue-500 rounded-t-lg hover:from-blue-700 hover:to-blue-600 transition-all duration-300 cursor-pointer shadow-sm hover:shadow-md transform hover:scale-105"
                style={{ 
                  height: count === 0 ? '2px' : `${Math.max((count / maxValue) * 160, 8)}px`
                }}
                title={`${weekDays[index]}: ${count} lesson${count !== 1 ? 's' : ''}`}
              >
                {/* Value display on top of bar for larger values */}
                {count > 0 && (
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs font-semibold text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    {count}
                  </div>
                )}
              </div>
            </div>
            
            {/* Day label */}
            <div className="text-sm mt-3 text-gray-600 font-medium group-hover:text-blue-600 transition-colors duration-200">
              {weekDays[index]}
            </div>
          </div>
        ))}
      </div>
      
      {/* Bottom axis line */}
      <div className="h-px bg-gray-200 mt-2"></div>
      
      {/* Summary stats */}
      <div className="flex justify-between items-center mt-4 text-sm text-gray-500">
        <span>Total: {data.reduce((sum, count) => sum + count, 0)} lessons</span>
        <span>Avg: {Math.round((data.reduce((sum, count) => sum + count, 0) / 7) * 10) / 10} per day</span>
      </div>
    </div>
  );
};

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month);
    const monthName = currentMonth.toLocaleString('default', { month: 'long' });
    
    const calendarDays = [];
    for (let i = 0; i < firstDayOfMonth; i++) calendarDays.push(0);
    for (let i = 1; i <= daysInMonth; i++) calendarDays.push(i);
    
    const getLessonsCount = (day) => {
      if (day === 0) return 0;
      const date = new Date(year, month, day);
      return lessons.filter(lesson => 
        formatDate(new Date(lesson.dateTime)) === formatDate(date)
      ).length;
    };
    
    const isToday = (day) => {
      const today = new Date();
      return day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
    };

    const isSelected = (day) => {
      if (!selectedDate || day === 0) return false;
      return day === selectedDate.getDate() && 
             month === selectedDate.getMonth() && 
             year === selectedDate.getFullYear();
    };

    return (
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <button 
            onClick={() => handleMonthChange(-1)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h3 className="font-semibold text-xl text-gray-900">{monthName} {year}</h3>
          <button 
            onClick={() => handleMonthChange(1)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        
        <div className="grid grid-cols-7 gap-2 text-center text-sm font-medium text-gray-500 mb-4">
          {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
            <div key={day} className="py-2">{day.slice(0, 3)}</div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-2">
          {calendarDays.map((day, index) => {
            const lessonsCount = getLessonsCount(day);
            return (
              <div 
                key={index} 
                onClick={() => handleDateClick(day)}
                className={`min-h-16 p-2 rounded-lg border transition-all ${
                  day === 0 
                    ? 'bg-gray-50 border-transparent'
                    : 'cursor-pointer hover:bg-blue-50 hover:border-blue-200 border-gray-200'
                } ${isToday(day) ? 'bg-blue-100 border-blue-300 ring-2 ring-blue-200' : ''}
                ${isSelected(day) ? 'bg-blue-50 border-blue-300' : ''}
              `}
              >
                {day !== 0 && (
                  <div className="flex flex-col h-full">
                    <div className={`text-right text-sm mb-1 ${
                      isToday(day) ? 'font-bold text-blue-600' : 'text-gray-700'
                    }`}>
                      {day}
                    </div>
                    {lessonsCount > 0 && (
                      <div className="flex-1 flex items-center justify-center">
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span className="text-xs text-blue-600 font-medium">{lessonsCount}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    if (!selectedDate) return null;
    
    const dateString = selectedDate.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });

    return (
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center mb-6">
          <button 
            onClick={() => setActiveView('calendar')}
            className="mr-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h3 className="font-semibold text-xl text-gray-900">{dateString}</h3>
          <div className="ml-auto">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center">
              <Plus className="w-4 h-4 mr-2" />
              Add Lesson
            </button>
          </div>
        </div>
        
        {selectedLessons.length > 0 ? (
          <div className="space-y-4">
            {selectedLessons
              .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime))
              .map(lesson => (
                <LessonCard 
                  key={lesson.id} 
                  lesson={lesson} 
                  onClick={() => handleLessonClick(lesson)}
                />
              ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No lessons scheduled</h4>
            <p className="text-gray-500 mb-4">You don't have any lessons scheduled for this day.</p>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              Schedule a Lesson
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderAllLessons = () => (
    <div className="space-y-6">
      <FilterBar />
      
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="font-semibold text-lg text-gray-900">All Lessons ({filteredLessons.length})</h3>
          <div className="flex items-center space-x-2">
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field);
                setSortOrder(order);
              }}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1"
            >
              <option value="date-desc">Date (Newest)</option>
              <option value="date-asc">Date (Oldest)</option>
              <option value="student-asc">Student (A-Z)</option>
              <option value="student-desc">Student (Z-A)</option>
              <option value="subject-asc">Subject (A-Z)</option>
              <option value="status-asc">Status</option>
            </select>
            <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
              <Download className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLessons.map(lesson => {
                const lessonDate = new Date(lesson.dateTime);
                return (
                  <tr key={lesson.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {lessonDate.toLocaleDateString()}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatTime(lessonDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {lesson.subject}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                          <span className="text-sm font-medium text-blue-600">
                            {lesson.studentFirstName?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          {lesson.studentFirstName} {lesson.studentLastName}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {lesson.duration} min
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={lesson.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLessonClick(lesson);
                          }}
                          className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          className="text-gray-600 hover:text-gray-800 p-1 rounded hover:bg-gray-50"
                          title="Edit Lesson"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                          title="Delete Lesson"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {filteredLessons.length === 0 && (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">No lessons found</h4>
              <p className="text-gray-500">Try adjusting your filters or search terms.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const DetailRow = ({ icon, label, children, clickable = false, onClick }) => (
    <div 
      className={`flex items-start ${clickable ? 'cursor-pointer hover:bg-gray-50 p-3 -m-3 rounded-lg transition-colors' : ''}`}
      onClick={onClick}
    >
      <div className="text-gray-400 mr-3 mt-1 flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-500 mb-1">{label}</div>
        <div className="text-gray-900">{children}</div>
      </div>
    </div>
  );

  const renderLessonModal = () => {
    if (!selectedLesson) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center border-b p-6 sticky top-0 bg-white z-10 rounded-t-xl">
            <div>
              <h3 className="font-semibold text-xl text-gray-900">{selectedLesson.subject}</h3>
              <p className="text-sm text-gray-500 mt-1">Lesson Details</p>
            </div>
            <button 
              onClick={closeModal}
              className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <DetailRow icon={<CalendarIcon className="w-5 h-5" />} label="Date">
                {new Date(selectedLesson.dateTime).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </DetailRow>
              
              <DetailRow icon={<Clock className="w-5 h-5" />} label="Time">
                {`${formatTime(new Date(selectedLesson.dateTime))} - ${
                  formatTime(new Date(selectedLesson.endTime || new Date(selectedLesson.dateTime).getTime() + selectedLesson.duration * 60000))
                }`}
              </DetailRow>
              
              <DetailRow icon={<Clock className="w-5 h-5" />} label="Duration">
                {selectedLesson.duration} minutes
              </DetailRow>
              
              <DetailRow icon={<BookOpen className="w-5 h-5" />} label="Subject">
                {selectedLesson.subject}
              </DetailRow>
            </div>
            
            <DetailRow icon={<Info className="w-5 h-5" />} label="Status">
              <StatusBadge status={selectedLesson.status} />
            </DetailRow>
            
            <DetailRow 
              icon={<User className="w-5 h-5" />} 
              label="Student"
              clickable
              onClick={() => {
                closeModal();
                handleOpenStudentPanel(selectedLesson.studentId);
              }}
            >
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <span className="text-sm font-medium text-blue-600">
                    {selectedLesson.studentFirstName?.charAt(0) || '?'}
                  </span>
                </div>
                <span className="text-blue-600 hover:text-blue-800 font-medium">
                  {selectedLesson.studentFirstName} {selectedLesson.studentLastName}
                </span>
              </div>
            </DetailRow>
            
            {selectedLesson.notes && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                <p className="text-gray-700 text-sm">{selectedLesson.notes}</p>
              </div>
            )}
          </div>
          
          <div className="border-t p-6 flex justify-end space-x-3 sticky bottom-0 bg-white rounded-b-xl">
            <button
              onClick={closeModal}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Close
            </button>
            <button
              className="px-4 py-2 bg-gray-600 text-white hover:bg-gray-700 rounded-lg transition-colors flex items-center"
              onClick={() => {
                closeModal();
                handleOpenStudentPanel(selectedLesson.studentId);
              }}
            >
              <User className="w-4 h-4 mr-2" />
              View Student
            </button>
            <button
              className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors flex items-center"
              onClick={() => {
                closeModal();
              }}
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Lesson
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-gray-50 ">
        <div className="flex justify-center items-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your lessons...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-gray-50  p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700 max-w-md mx-auto">
          <div className="flex items-center mb-4">
            <AlertCircle className="w-6 h-6 mr-3 text-red-500" />
            <h3 className="font-medium text-red-900">Error Loading Lessons</h3>
          </div>
          <p className="text-red-700 mb-4">{error}</p>
          <button 
            onClick={() => fetchLessons()}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 ">
      {/* View selector tabs */}
      <div className="flex  border-b border-gray-200 sticky top-0 z-10">
        <div className="flex">
          {viewOptions.map(view => (
            <button
              key={view.id}
              onClick={() => setActiveView(view.id)}
              className={`w-fit py-2 px-4 flex items-center whitespace-nowrap border-b-2 transition-colors ${
                activeView === view.id 
                  ? 'border-blue-500 text-blue-600 bg-blue-50' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{view.icon}</span>
              <span className="font-medium">{view.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className="p-6">
        {activeView === 'dashboard' && renderDashboard()}
        {activeView === 'calendar' && renderCalendar()}
        {activeView === 'day' && renderDayView()}
        {activeView === 'lessons' && renderAllLessons()}
      </div>

      {/* Modals */}
      {selectedLesson && renderLessonModal()}
      {isStudentPanelOpen && studentForPanel && (
        <StudentDetailPanel 
          student={studentForPanel} 
          onClose={handleClosePanel}
          onSave={handleClosePanel}
        />
      )}
    </div>
  );
};

export default LessonCalendarDashboard;